import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Analytics } from '../types';

const getCurrentTenantId = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return null;
  const { data } = await supabase
    .from('tenant_users')
    .select('tenant_id')
    .eq('auth_user_id', session.user.id)
    .maybeSingle();
  return data?.tenant_id ?? null;
};

export const useAnalytics = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateAnalytics();
  }, []);

  const calculateAnalytics = async () => {
    try {
      setLoading(true);

      const tenantId = await getCurrentTenantId();
      const cacheKey = `dashboard_analytics_v2_${tenantId ?? 'unknown'}`;

      // Check cache first
      const { data: cached } = await supabase
        .from('analytics_cache')
        .select('*')
        .eq('metric_name', cacheKey)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();
        
      if (cached) {
        setAnalytics(cached.metric_value as Analytics);
        setLoading(false);
        return;
      }
      
      // Calculate fresh analytics
      const [
        appointmentsResult,
        riskDistributionResult,
        departmentStatsResult,
        messagesResult
      ] = await Promise.all([
        // Total appointments and no-show rate
        supabase
          .from('appointments')
          .select('status, appointment_date'),
        
        // Risk distribution
        supabase
          .from('appointments')
          .select('risk_level')
          .eq('status', 'scheduled'),
        
        // Department statistics
        supabase
          .from('appointments')
          .select('department, status'),
        
        // Messages sent
        supabase
          .from('messages')
          .select('status')
      ]);
      
      const appointments = appointmentsResult.data || [];
      const totalAppointments = appointments.length;
      const noShows = appointments.filter(a => a.status === 'no-show').length;
      const noShowRate = totalAppointments > 0 ? (noShows / totalAppointments) * 100 : 0;
      
      // Risk distribution
      const riskData = riskDistributionResult.data || [];
      const appointmentsByRisk = {
        low: riskData.filter(r => r.risk_level === 'low').length,
        medium: riskData.filter(r => r.risk_level === 'medium').length,
        high: riskData.filter(r => r.risk_level === 'high').length
      };
      
      // Department statistics
      const deptData = departmentStatsResult.data || [];
      const deptStats = new Map();
      
      deptData.forEach(apt => {
        if (!deptStats.has(apt.department)) {
          deptStats.set(apt.department, { total: 0, noShows: 0 });
        }
        const stats = deptStats.get(apt.department);
        stats.total++;
        if (apt.status === 'no-show') stats.noShows++;
      });
      
      const noShowByDepartment = Array.from(deptStats.entries()).map(([dept, stats]) => ({
        department: dept,
        rate: stats.total > 0 ? (stats.noShows / stats.total) * 100 : 0,
        total: stats.total
      }));
      
      // Messages
      const messages = messagesResult.data || [];
      const messagesSent = messages.length;
      const deliveredMessages = messages.filter(m => m.status === 'delivered').length;
      const messageDeliveryRate = messagesSent > 0 ? (deliveredMessages / messagesSent) * 100 : 0;
      
      const weeklyStats = new Map<string, { total: number; noShows: number }>();
      appointments.forEach(appointment => {
        const date = new Date(`${appointment.appointment_date}T00:00:00Z`);
        const dayOfWeek = date.getUTCDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        date.setUTCDate(date.getUTCDate() - daysFromMonday);
        const week = date.toISOString().split('T')[0];
        const stats = weeklyStats.get(week) ?? { total: 0, noShows: 0 };
        stats.total += 1;
        if (appointment.status === 'no-show') stats.noShows += 1;
        weeklyStats.set(week, stats);
      });

      const weeklyTrends = Array.from(weeklyStats.entries())
        .sort(([firstWeek], [secondWeek]) => firstWeek.localeCompare(secondWeek))
        .slice(-5)
        .map(([week, stats]) => ({
          week,
          noShowRate: Math.round((stats.noShows / stats.total) * 1000) / 10,
          totalAppointments: stats.total
        }));

      const analyticsData: Analytics = {
        totalAppointments,
        noShowRate: Math.round(noShowRate * 10) / 10,
        previousNoShowRate: weeklyTrends.length > 1
          ? weeklyTrends[weeklyTrends.length - 2].noShowRate
          : Math.round(noShowRate * 10) / 10,
        messagesSent,
        messageDeliveryRate: Math.round(messageDeliveryRate * 10) / 10,
        appointmentsByRisk,
        noShowByDepartment,
        weeklyTrends
      };
      
      // Cache the results for 1 hour
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 1);
      
      await supabase
        .from('analytics_cache')
        .upsert({
          metric_name: cacheKey,
          metric_value: analyticsData,
          expires_at: expiresAt.toISOString(),
          tenant_id: tenantId ?? undefined
        }, {
          onConflict: 'metric_name'
        });
      
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error calculating analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return { analytics, loading };
};